import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Page,
  Card,
  BlockStack,
  Box,
  Button,
  Text,
  InlineStack,
  SkeletonDisplayText,
  SkeletonBodyText,
} from '@shopify/polaris';
import { PricingCard } from '../components/PricingCard';
import { fetchWithSessionToken } from '../lib/appBridge';
import { ExternalMinor } from '@shopify/polaris-icons';
import { trackAnalyticsEvent } from '../lib/analytics';

interface PricingPageProps {
  shop: string;
  host: string;
}

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'accepted', 'trialing', 'pending', 'pending_active']);
const BILLING_RETURN_FLAG_KEY = 'genseo_shopify_billing_return';
const BILLING_RETURN_WINDOW_MS = 30 * 60 * 1000;
const ENTITLEMENT_RETRY_DELAYS_MS = [0, 800, 1800];

const isSubscriptionActive = (hasSubscription: boolean, status?: string | null) => {
  const normalizedStatus = (status || '').toLowerCase();
  if (ACTIVE_SUBSCRIPTION_STATUSES.has(normalizedStatus)) {
    return true;
  }
  if (normalizedStatus.length > 0) {
    return false;
  }
  return hasSubscription;
};

const normalizeSubscriptionStatusLabel = (status?: string | null) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'cancelled' || normalized === 'canceled') return 'inactive';
  return normalized;
};

const wait = (ms: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const PricingPage: React.FC<PricingPageProps> = ({ shop, host }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [isOpeningPricing, setIsOpeningPricing] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [subscriptionName, setSubscriptionName] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [redirectTarget, setRedirectTarget] = useState<string | null>(null);
  const [isLeavingForManagedPricing, setIsLeavingForManagedPricing] = useState(false);
  const chargeId = searchParams.get('charge_id') || '';

  useEffect(() => {
    if (!shop) return;
    trackAnalyticsEvent('billing_pricing_viewed', {
      route: '/pricing',
      shopDomain: normalizeShopDomain(shop),
    });
  }, [shop]);

  const markManagedPricingReturn = useCallback(() => {
    try {
      window.localStorage.setItem(BILLING_RETURN_FLAG_KEY, String(Date.now()));
    } catch {
      // ignore storage issues
    }
  }, []);

  const consumeManagedPricingReturn = useCallback(() => {
    try {
      const raw = window.localStorage.getItem(BILLING_RETURN_FLAG_KEY);
      if (!raw) return false;
      window.localStorage.removeItem(BILLING_RETURN_FLAG_KEY);
      const timestamp = Number(raw);
      if (!Number.isFinite(timestamp)) return false;
      return Date.now() - timestamp <= BILLING_RETURN_WINDOW_MS;
    } catch {
      return false;
    }
  }, []);

  const normalizeShopDomain = (rawShop: string) => {
    const cleaned = rawShop.trim().toLowerCase().replace(/^https?:\/\//, '');
    const withoutQuery = cleaned.split('?')[0];
    const adminStoreMatch = withoutQuery.match(/admin\.shopify\.com\/store\/([^/]+)/);
    if (adminStoreMatch?.[1]) {
      return `${adminStoreMatch[1]}.myshopify.com`;
    }
    const hostOnly = withoutQuery.split('/')[0];
    if (hostOnly.includes('.myshopify.com')) {
      return hostOnly;
    }
    if (!hostOnly.includes('.')) {
      return `${hostOnly}.myshopify.com`;
    }
    return hostOnly;
  };

  const fetchSubscriptionStatus = async (shopDomain: string, hostParam: string) => {
    const params = new URLSearchParams({ shop: shopDomain, host: hostParam });
    const res = await fetchWithSessionToken(hostParam, `/api/subscription-status?${params.toString()}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false as const, data };
    }
    return { ok: true as const, data };
  };

  const fetchNativeStatus = async (shopDomain: string, hostParam: string) => {
    const params = new URLSearchParams({ shop: shopDomain, host: hostParam });
    const res = await fetchWithSessionToken(hostParam, `/api/native-status?${params.toString()}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false as const, data };
    }
    return { ok: true as const, data };
  };

  const resolveEntitlementGate = useCallback(
    async (retryForReturn = false) => {
      const retryDelays = retryForReturn ? ENTITLEMENT_RETRY_DELAYS_MS : [0];
      let lastResult = {
        nativeOnboardingComplete: null as boolean | null,
        nativeConnected: false,
        hasSubscription: false,
        subscriptionStatus: null as string | null,
        subscriptionName: null as string | null,
        active: false,
      };

      const shopDomain = normalizeShopDomain(shop);
      for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
        const delay = retryDelays[attempt];
        if (delay > 0) {
          await wait(delay);
        }

        const [subscriptionResult, nativeResult] = await Promise.all([
          fetchSubscriptionStatus(shopDomain, host),
          fetchNativeStatus(shopDomain, host),
        ]);

        if (!nativeResult.ok) {
          return { ok: false as const, error: nativeResult.data?.error || 'Failed to check connection status', result: lastResult };
        }

        const nativeHasSubscription = Boolean(nativeResult.data?.hasSubscription);
        const nativeSubscriptionStatus = nativeResult.data?.subscriptionStatus ?? null;
        const nativeSubscriptionName = nativeResult.data?.subscriptionName ?? null;
        const nativeOnboardingComplete = nativeResult.data?.projectOnboardingComplete ?? null;
        const nativeConnected = Boolean(nativeResult.data?.connected);

        const resolvedHasSubscription =
          nativeHasSubscription || Boolean(subscriptionResult.ok ? subscriptionResult.data?.hasSubscription : false);
        const resolvedStatus =
          nativeSubscriptionStatus || (subscriptionResult.ok ? subscriptionResult.data?.subscriptionStatus : null) || null;
        const resolvedName =
          nativeSubscriptionName || (subscriptionResult.ok ? subscriptionResult.data?.subscriptionName : null) || null;
        const active = isSubscriptionActive(resolvedHasSubscription, resolvedStatus);

        lastResult = {
          nativeOnboardingComplete,
          nativeConnected,
          hasSubscription: resolvedHasSubscription,
          subscriptionStatus: resolvedStatus,
          subscriptionName: resolvedName,
          active,
        };

        if (active) {
          break;
        }
      }

      return { ok: true as const, result: lastResult };
    },
    [host, shop]
  );

  const refreshStatus = async (showErrors = true) => {
    if (!shop || !host) return;
    if (isLeavingForManagedPricing) return;
    try {
      setIsCheckingConnection(true);
      if (showErrors) setConnectionError(null);

      const returnedFromManagedPricing = consumeManagedPricingReturn();
      const returnedFromChargeApproval = Boolean(chargeId);
      const gate = await resolveEntitlementGate(
        returnedFromManagedPricing || returnedFromChargeApproval
      );

      if (!gate.ok) {
        if (showErrors) {
          throw new Error(gate.error || 'Failed to check connection status');
        }
        return;
      }

      setHasSubscription(gate.result.hasSubscription);
      setSubscriptionStatus(gate.result.subscriptionStatus);
      setSubscriptionName(gate.result.subscriptionName);

      if (gate.result.active) {
        if (gate.result.nativeOnboardingComplete === true) {
          setRedirectTarget('/');
          return;
        }
        setRedirectTarget('/onboarding-info');
        return;
      }

      if (gate.result.nativeConnected) {
        setRedirectTarget('/');
        return;
      }
    } catch (error: any) {
      if (showErrors) {
        setConnectionError(error?.message || 'Failed to check connection status');
      }
    } finally {
      setIsCheckingConnection(false);
      setInitialLoad(false);
    }
  };

  const checkConnection = async () => {
    await refreshStatus(true);
  };

  const openManagedPricing = async () => {
    if (!shop || !host) return;
    try {
      setIsOpeningPricing(true);
      setPlanError(null);
      setIsLeavingForManagedPricing(true);
      const shopDomain = normalizeShopDomain(shop);
      trackAnalyticsEvent('plan_selected', {
        route: '/pricing',
        shopDomain,
        plan: 'BUSINESS',
      });
      trackAnalyticsEvent('shopify_billing_started', {
        route: '/pricing',
        shopDomain,
        plan: 'BUSINESS',
      });
      const res = await fetchWithSessionToken(host, '/api/billing/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop: shopDomain,
          host,
          plan: 'BUSINESS',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to open Shopify pricing');
      }

      if (data?.active) {
        setIsLeavingForManagedPricing(false);
        setIsOpeningPricing(false);
        await refreshStatus(false);
        return;
      }

      const managedPricingUrl = data?.managedPricingUrl as string | undefined;
      if (!managedPricingUrl) {
        throw new Error('Managed pricing URL not configured');
      }

      markManagedPricingReturn();

      if (window.top) {
        window.top.location.href = managedPricingUrl;
      } else {
        window.location.href = managedPricingUrl;
      }
    } catch (error: any) {
      setIsLeavingForManagedPricing(false);
      setPlanError(error?.message || 'Failed to open Shopify pricing');
    } finally {
      setIsOpeningPricing(false);
    }
  };

  useEffect(() => {
    if (!shop || !host) return;
    refreshStatus(false);
  }, [shop, host, isLeavingForManagedPricing, chargeId]);

  useEffect(() => {
    if (!redirectTarget || isLeavingForManagedPricing) return;
    navigate(redirectTarget, { replace: true });
  }, [redirectTarget, navigate, isLeavingForManagedPricing]);

  const isProPlan = subscriptionName
    ? subscriptionName.toLowerCase().includes('pro')
    : false;
  const hasAnySubscription = hasSubscription || Boolean(subscriptionName);
  const hasActiveEntitlement = isSubscriptionActive(hasAnySubscription, subscriptionStatus);
  const showInitialSkeleton = initialLoad || isCheckingConnection || Boolean(redirectTarget);

  if (showInitialSkeleton) {
    return (
      <Page>
        <Card>
          <div style={{ marginTop: '1rem' }}>
            <Box padding="800">
              <BlockStack gap="400">
                <SkeletonDisplayText size="large" />
                <SkeletonBodyText lines={4} />
                <SkeletonBodyText lines={2} />
              </BlockStack>
            </Box>
          </div>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <BlockStack gap="400">
        <div className="section_header" style={{ paddingTop: '1rem' }}>
          <Box paddingBlockStart="600" paddingBlockEnd="200">
            <BlockStack gap="100">
              <Text as="h1" variant="headingLg" alignment="center">
                Plans
              </Text>
              <Text as="p" tone="subdued" variant="bodySm" alignment="center">
                Subscribe to a plan that fits your needs
              </Text>
              <Text as="p" tone="subdued" variant="bodySm" alignment="center">
                After you select a plan in Shopify, return here and click “Check connection”.
              </Text>
            </BlockStack>
          </Box>
        </div>

        <div className="pricing-card-wrap" style={{ marginBottom: '0.5rem', width: '100%' }}>
          <div style={{ maxWidth: '19rem', marginLeft: 'auto', marginRight: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <BlockStack gap="300">
            <PricingCard
              title="Genseo Pro"
              featuredText="Most Popular"
              description="Built for Shopify stores that want automated SEO content at scale."
              features={[
                '30 SEO/GEO optimized articles',
                'Personalized keyword strategy',
                '30 keyword research credits',
                'Automatic publishing to Shopify',
                'AI images and internal linking',
                'Supports 75+ languages',
              ]}
              price="$99"
              frequency="month"
              button={{
                content: hasActiveEntitlement
                  ? 'Continue to Connect'
                  : hasAnySubscription
                  ? isProPlan
                    ? 'Continue with Pro'
                    : 'Upgrade to Pro'
                  : 'Subscribe to Pro',
                props: {
                  variant: 'primary',
                  loading: isOpeningPricing,
                  disabled: isOpeningPricing,
                  onClick: hasActiveEntitlement ? checkConnection : openManagedPricing,
                },
              }}
            />

            <InlineStack align="center">
              <div style={{ marginTop: '0.5rem' }}>
                <Button
                  variant="secondary"
                  icon={ExternalMinor}
                  onClick={() => {
                    trackAnalyticsEvent('genseo_connection_started', {
                      route: '/pricing',
                      shopDomain: normalizeShopDomain(shop),
                      destination: 'genseo_integrations',
                    });
                    trackAnalyticsEvent('external_dashboard_docs_links_opened', {
                      route: '/pricing',
                      shopDomain: normalizeShopDomain(shop),
                      destination: 'genseo_integrations',
                    });
                    window.open('https://app.genseo.co/integrations', '_blank');
                  }}
                >
                  Already have a subscription? Connect now
                </Button>
              </div>
            </InlineStack>

            <InlineStack align="center">
              <Button
                variant="plain"
                size="slim"
                onClick={checkConnection}
                loading={isCheckingConnection}
                disabled={isCheckingConnection}
              >
                Check connection
              </Button>
            </InlineStack>

            {planError && (
              <Text as="p" tone="critical" variant="bodySm" alignment="center">
                {planError}
              </Text>
            )}

            {connectionError && (
              <Text as="p" tone="critical" variant="bodySm" alignment="center">
                {connectionError}
              </Text>
            )}

            {subscriptionStatus && (
              <Text as="p" tone="subdued" variant="bodySm" alignment="center">
                {subscriptionName ? `${subscriptionName} • ` : ''}Subscription status: {normalizeSubscriptionStatusLabel(subscriptionStatus)}
              </Text>
            )}

            {!subscriptionStatus && hasAnySubscription && (
              <Text as="p" tone="subdued" variant="bodySm" alignment="center">
                Subscription active — connect your shop in Genseo and then click “Check connection”.
              </Text>
            )}
            </BlockStack>
          </div>
        </div>
      </BlockStack>
    </Page>
  );
};

export default PricingPage;
